import React, { useRef, useState } from "react";
import { Field, FieldArray } from "redux-form";
import { IconButton } from "@mui/material";

import { customInput } from "../../../../../common/custom-input";
import styles from "../../../SwmsMastersStyle.module.css";
import layoutStyles from "../../EmergencyProceduresLayout.module.css";

const getDefaultPhase = () => ({
  name: "",
  steps: [""]
});

function PhaseStepRows({ fields, phaseDisplayIndex, loading }) {
  const dragStepIndexRef = useRef(null);
  const [draggingStepIndex, setDraggingStepIndex] = useState(null);
  const [hoveredStepIndex, setHoveredStepIndex] = useState(null);

  return (
    <div className={layoutStyles.PerformRescueStepRowsWrap}>
      {fields.length === 0 ? (
        <div className={layoutStyles.PerformRescueEmptyStepText}>
          No steps added yet.
        </div>
      ) : null}

      {fields.length > 0 ? (
        <div className={layoutStyles.PerformRescueStepsContainer}>
          {fields.map((member, stepIndex) => (
            <div
              key={member}
              draggable={!loading}
              onDragStart={(event) => {
                event.stopPropagation();
                if (loading) {
                  event.preventDefault();
                  return;
                }
                dragStepIndexRef.current = stepIndex;
                setDraggingStepIndex(stepIndex);
                setHoveredStepIndex(null);
                if (event.dataTransfer) {
                  event.dataTransfer.effectAllowed = "move";
                  event.dataTransfer.setData("text/plain", String(stepIndex));
                }
              }}
              onDragEnter={(event) => {
                event.stopPropagation();
                if (loading || draggingStepIndex === null || draggingStepIndex === stepIndex) {
                  return;
                }
                event.preventDefault();
                setHoveredStepIndex(stepIndex);
              }}
              onDragOver={(event) => {
                event.stopPropagation();
                if (dragStepIndexRef.current === null || loading || draggingStepIndex === stepIndex) {
                  return;
                }
                event.preventDefault();
                if (hoveredStepIndex !== stepIndex) {
                  setHoveredStepIndex(stepIndex);
                }
                if (event.dataTransfer) {
                  event.dataTransfer.dropEffect = "move";
                }
              }}
              onDragLeave={(event) => {
                event.stopPropagation();
                if (!event.currentTarget.contains(event.relatedTarget)) {
                  setHoveredStepIndex((previous) => (previous === stepIndex ? null : previous));
                }
              }}
              onDrop={(event) => {
                event.stopPropagation();
                if (loading) {
                  return;
                }
                event.preventDefault();
                const fromIndex = dragStepIndexRef.current;
                if (fromIndex === null || fromIndex === stepIndex) {
                  dragStepIndexRef.current = null;
                  setDraggingStepIndex(null);
                  setHoveredStepIndex(null);
                  return;
                }
                fields.move(fromIndex, stepIndex);
                dragStepIndexRef.current = null;
                setDraggingStepIndex(null);
                setHoveredStepIndex(null);
              }}
              onDragEnd={(event) => {
                event.stopPropagation();
                dragStepIndexRef.current = null;
                setDraggingStepIndex(null);
                setHoveredStepIndex(null);
              }}
              className={layoutStyles.PerformRescueStepRow}
              style={{
                borderBottom: stepIndex < fields.length - 1 ? "1px solid #F0ECFA" : "none",
                border: draggingStepIndex !== null && draggingStepIndex !== stepIndex && hoveredStepIndex === stepIndex
                  ? "1px solid #7C3AED"
                  : "1px solid transparent",
                boxShadow: draggingStepIndex !== null && draggingStepIndex !== stepIndex && hoveredStepIndex === stepIndex
                  ? "0 0 0 2px rgba(124, 58, 237, 0.14)"
                  : "none",
                transition: "border-color 120ms ease, box-shadow 120ms ease"
              }}
            >
              <span className={layoutStyles.PerformRescueDragHandle} style={{ cursor: loading ? "default" : "grab" }} title="Drag to reorder step">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512" width="12" height="12">
                  <path fill="currentColor" d="M40 352l48 0c22.1 0 40 17.9 40 40l0 48c0 22.1-17.9 40-40 40l-48 0c-22.1 0-40-17.9-40-40l0-48c0-22.1 17.9-40 40-40zm192 0l48 0c22.1 0 40 17.9 40 40l0 48c0 22.1-17.9 40-40 40l-48 0c-22.1 0-40-17.9-40-40l0-48c0-22.1 17.9-40 40-40zM40 320c-22.1 0-40-17.9-40-40l0-48c0-22.1 17.9-40 40-40l48 0c22.1 0 40 17.9 40 40l0 48c0 22.1-17.9 40-40 40l-48 0zM232 192l48 0c22.1 0 40 17.9 40 40l0 48c0 22.1-17.9 40-40 40l-48 0c-22.1 0-40-17.9-40-40l0-48c0-22.1 17.9-40 40-40zM40 160c-22.1 0-40-17.9-40-40L0 72C0 49.9 17.9 32 40 32l48 0c22.1 0 40 17.9 40 40l0 48c0 22.1-17.9 40-40 40l-48 0zM232 32l48 0c22.1 0 40 17.9 40 40l0 48c0 22.1-17.9 40-40 40l-48 0c-22.1 0-40-17.9-40-40l0-48c0-22.1 17.9-40 40-40z" />
                </svg>
              </span>

              <span className={layoutStyles.PerformRescueStepIndexChip}>
                {phaseDisplayIndex}.{stepIndex + 1}
              </span>

              <div className={layoutStyles.PerformRescueFlexGrow}>
                <Field
                  name={member}
                  component={customInput}
                  className={styles.TextField}
                  disabled={loading}
                  placeholder="Action Step"
                  autoComplete="off"
                />
              </div>

              <IconButton
                className={`p-0 ${layoutStyles.IconButtonAutoHeight}`}
                onClick={() => fields.remove(stepIndex)}
                disabled={loading}
                title="Delete step"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" className={`${styles.DeleteIcon} ${layoutStyles.MediaDeleteIcon}`}>
                  <path fill="currentColor" d="M136.7 5.9L128 32 32 32C14.3 32 0 46.3 0 64S14.3 96 32 96l384 0c17.7 0 32-14.3 32-32s-14.3-32-32-32l-96 0-8.7-26.1C306.9-7.2 294.7-16 280.9-16L167.1-16c-13.8 0-26 8.8-30.4 21.9zM416 144L32 144 53.1 467.1C54.7 492.4 75.7 512 101 512L347 512c25.3 0 46.3-19.6 47.9-44.9L416 144z" />
                </svg>
              </IconButton>
            </div>
          ))}
        </div>
      ) : null}

      <button
        type="button"
        disabled={loading}
        onClick={() => fields.push("")}
        className={layoutStyles.PerformRescueAddStepButton}
      >
        <svg height="11px" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512">
          <path fill="currentColor" d="M256 80C256 62.3 241.7 48 224 48C206.3 48 192 62.3 192 80L192 192L80 192C62.3 192 48 206.3 48 224C48 241.7 62.3 256 80 256L192 256L192 368C192 385.7 206.3 400 224 400C241.7 400 256 385.7 256 368L256 256L368 256C385.7 256 400 241.7 400 224C400 206.3 385.7 192 368 192L256 192L256 80z" />
        </svg>
        Add Step
      </button>
    </div>
  );
}

function PhaseRows({ fields, loading }) {
  const dragPhaseIndexRef = useRef(null);
  const [draggingPhaseIndex, setDraggingPhaseIndex] = useState(null);
  const [hoveredPhaseIndex, setHoveredPhaseIndex] = useState(null);

  return (
    <div>
      {fields.length === 0 ? (
        <div className={layoutStyles.PerformRescueEmptyPhaseText}>
          No phases added
        </div>
      ) : null}

      {fields.map((member, phaseIndex) => (
        <div
          key={member}
          draggable={!loading}
          onDragStart={(event) => {
            if (loading) {
              event.preventDefault();
              return;
            }
            dragPhaseIndexRef.current = phaseIndex;
            setDraggingPhaseIndex(phaseIndex);
            setHoveredPhaseIndex(null);
            if (event.dataTransfer) {
              event.dataTransfer.effectAllowed = "move";
              event.dataTransfer.setData("text/plain", String(phaseIndex));
            }
          }}
          onDragEnter={(event) => {
            if (loading || draggingPhaseIndex === null || draggingPhaseIndex === phaseIndex) {
              return;
            }
            event.preventDefault();
            setHoveredPhaseIndex(phaseIndex);
          }}
          onDragOver={(event) => {
            if (dragPhaseIndexRef.current === null || loading || draggingPhaseIndex === phaseIndex) {
              return;
            }
            event.preventDefault();
            if (hoveredPhaseIndex !== phaseIndex) {
              setHoveredPhaseIndex(phaseIndex);
            }
            if (event.dataTransfer) {
              event.dataTransfer.dropEffect = "move";
            }
          }}
          onDragLeave={(event) => {
            if (!event.currentTarget.contains(event.relatedTarget)) {
              setHoveredPhaseIndex((previous) => (previous === phaseIndex ? null : previous));
            }
          }}
          onDrop={(event) => {
            if (loading) {
              return;
            }
            event.preventDefault();
            const fromIndex = dragPhaseIndexRef.current;
            if (fromIndex === null || fromIndex === phaseIndex) {
              dragPhaseIndexRef.current = null;
              return;
            }
            fields.move(fromIndex, phaseIndex);
            dragPhaseIndexRef.current = null;
            setDraggingPhaseIndex(null);
            setHoveredPhaseIndex(null);
          }}
          onDragEnd={() => {
            dragPhaseIndexRef.current = null;
            setDraggingPhaseIndex(null);
            setHoveredPhaseIndex(null);
          }}
          className={layoutStyles.PerformRescuePhaseCard}
          style={{
            marginTop: "10px",
            border: draggingPhaseIndex !== null && draggingPhaseIndex !== phaseIndex && hoveredPhaseIndex === phaseIndex
              ? "1px solid #7C3AED"
              : "1px solid #E4E0F0",
            boxShadow: draggingPhaseIndex !== null && draggingPhaseIndex !== phaseIndex && hoveredPhaseIndex === phaseIndex
              ? "0 0 0 2px rgba(124, 58, 237, 0.18)"
              : "none",
            transition: "border-color 120ms ease, box-shadow 120ms ease"
          }}
        >
          <div className={layoutStyles.PerformRescuePhaseHeaderRow}>
            <span className={layoutStyles.PerformRescueDragHandle} style={{ cursor: loading ? "default" : "grab" }} title="Drag to reorder phase">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512" width="13" height="13">
                <path fill="currentColor" d="M40 352l48 0c22.1 0 40 17.9 40 40l0 48c0 22.1-17.9 40-40 40l-48 0c-22.1 0-40-17.9-40-40l0-48c0-22.1 17.9-40 40-40zm192 0l48 0c22.1 0 40 17.9 40 40l0 48c0 22.1-17.9 40-40 40l-48 0c-22.1 0-40-17.9-40-40l0-48c0-22.1 17.9-40 40-40zM40 320c-22.1 0-40-17.9-40-40l0-48c0-22.1 17.9-40 40-40l48 0c22.1 0 40 17.9 40 40l0 48c0 22.1-17.9 40-40 40l-48 0zM232 192l48 0c22.1 0 40 17.9 40 40l0 48c0 22.1-17.9 40-40 40l-48 0c-22.1 0-40-17.9-40-40l0-48c0-22.1 17.9-40 40-40zM40 160c-22.1 0-40-17.9-40-40L0 72C0 49.9 17.9 32 40 32l48 0c22.1 0 40 17.9 40 40l0 48c0 22.1-17.9 40-40 40l-48 0zM232 32l48 0c22.1 0 40 17.9 40 40l0 48c0 22.1-17.9 40-40 40l-48 0c-22.1 0-40-17.9-40-40l0-48c0-22.1 17.9-40 40-40z" />
              </svg>
            </span>

            <span className={layoutStyles.PerformRescuePhaseIndexChip}>
              {phaseIndex + 1}
            </span>

            <div className={layoutStyles.PerformRescueFlexGrow}>
              <Field
                name={`${member}.name`}
                component={customInput}
                className={styles.TextField}
                disabled={loading}
                placeholder="Phase Name"
                autoComplete="off"
              />
            </div>

            <IconButton
              className={`p-0 ${layoutStyles.IconButtonAutoHeight}`}
              onClick={() => fields.remove(phaseIndex)}
              disabled={loading}
              title="Delete phase"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" className={`${styles.DeleteIcon} ${layoutStyles.MediaDeleteIcon}`}>
                <path fill="currentColor" d="M136.7 5.9L128 32 32 32C14.3 32 0 46.3 0 64S14.3 96 32 96l384 0c17.7 0 32-14.3 32-32s-14.3-32-32-32l-96 0-8.7-26.1C306.9-7.2 294.7-16 280.9-16L167.1-16c-13.8 0-26 8.8-30.4 21.9zM416 144L32 144 53.1 467.1C54.7 492.4 75.7 512 101 512L347 512c25.3 0 46.3-19.6 47.9-44.9L416 144z" />
              </svg>
            </IconButton>
          </div>

          <FieldArray
            name={`${member}.steps`}
            component={PhaseStepRows}
            phaseDisplayIndex={phaseIndex + 1}
            loading={loading}
          />
        </div>
      ))}

      <button
        type="button"
        disabled={loading}
        onClick={() => fields.push(getDefaultPhase())}
        className={layoutStyles.AddSectionButton}
      >
        <svg height="12px" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512">
          <path fill="currentColor" d="M256 80C256 62.3 241.7 48 224 48C206.3 48 192 62.3 192 80L192 192L80 192C62.3 192 48 206.3 48 224C48 241.7 62.3 256 80 256L192 256L192 368C192 385.7 206.3 400 224 400C241.7 400 256 385.7 256 368L256 256L368 256C385.7 256 400 241.7 400 224C400 206.3 385.7 192 368 192L256 192L256 80z" />
        </svg>
        Add Phase
      </button>
    </div>
  );
}

export default function PerformRescueSection({ loading }) {
  return (
    <div className="container-fluid pb-2">
      <div className="row pt-2">
        <div className="col-12">
          <FieldArray name="phases" component={PhaseRows} loading={loading} />
        </div>
      </div>
    </div>
  );
}

